import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ArrowLeft, Eye, EyeOff, Monitor, Tablet, Smartphone, Globe, Save, ExternalLink } from 'lucide-react';
import { Spinner, Button, useToast, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@mawkaa/ui';
import { useBuilderStore } from '@/stores/builderStore';
import { useSitesStore } from '@/stores/sitesStore';
import { BlockPalette } from '@/features/builder/components/BlockPalette';
import { BuilderCanvas } from '@/features/builder/components/BuilderCanvas';
import { PropertiesPanel } from '@/features/builder/components/PropertiesPanel';
import { PagesList } from '@/features/builder/components/PagesList';
import { cn } from '@mawkaa/ui';
import type { BlockType } from '@mawkaa/types';

export function BuilderPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    site,
    blocks,
    selectedBlockId,
    isPreviewMode,
    devicePreview,
    loadSite,
    addBlock,
    moveBlock,
    savePage,
    setPreviewMode,
    setDevicePreview,
  } = useBuilderStore();
  const { publishSite, unpublishSite } = useSitesStore();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    if (siteId) {
      setIsLoading(true);
      loadSite(siteId).finally(() => setIsLoading(false));
    }
  }, [siteId, loadSite]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    // Adding a new block from palette
    if (active.id.toString().startsWith('palette-')) {
      const blockType = active.id.toString().replace('palette-', '') as BlockType;
      const overIndex = blocks.findIndex((b) => b.id === over.id);
      addBlock(blockType, overIndex >= 0 ? overIndex : undefined);
      return;
    }

    // Reordering existing blocks
    if (active.id !== over.id) {
      const oldIndex = blocks.findIndex((b) => b.id === active.id);
      const newIndex = blocks.findIndex((b) => b.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        moveBlock(oldIndex, newIndex);
      }
    }
  };

  const handleSave = async () => {
    if (!siteId) return;
    setIsSaving(true);
    try {
      await savePage();
      toast({ title: 'Sauvegardé !', description: 'Vos modifications ont été enregistrées.' });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de sauvegarder.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!siteId) return;
    setIsPublishing(true);
    try {
      // Save first
      await savePage();
      // Then publish
      const { url } = await publishSite(siteId);
      toast({
        title: 'Site publié !',
        description: (
          <div className="flex items-center gap-2">
            <span>Votre site est en ligne :</span>
            <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
              {url}
            </a>
          </div>
        ),
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de publier.',
        variant: 'destructive',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!site) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Site non trouvé</p>
      </div>
    );
  }

  const siteUrl = site ? `https://${site.subdomain}.mawkaa.tn` : '';

  return (
    <TooltipProvider>
      <div className="flex flex-col h-screen">
        {/* Builder Header */}
        <header className="h-14 bg-white border-b flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard/sites')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold">{site?.name}</h1>
              {site?.status === 'published' && (
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-muted-foreground hover:underline flex items-center gap-1"
                >
                  {siteUrl}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Device Preview */}
            <div className="flex items-center bg-muted rounded-lg p-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDevicePreview('desktop')}
                    className={cn(
                      'p-2 rounded transition-colors',
                      devicePreview === 'desktop' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                    )}
                  >
                    <Monitor className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Desktop</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDevicePreview('tablet')}
                    className={cn(
                      'p-2 rounded transition-colors',
                      devicePreview === 'tablet' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                    )}
                  >
                    <Tablet className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Tablette</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDevicePreview('mobile')}
                    className={cn(
                      'p-2 rounded transition-colors',
                      devicePreview === 'mobile' ? 'bg-white shadow-sm' : 'hover:bg-white/50'
                    )}
                  >
                    <Smartphone className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Mobile</TooltipContent>
              </Tooltip>
            </div>

            {/* Preview Toggle */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={isPreviewMode ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setPreviewMode(!isPreviewMode)}
                >
                  {isPreviewMode ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isPreviewMode ? 'Quitter aperçu' : 'Aperçu'}
              </TooltipContent>
            </Tooltip>

            {/* Save Button */}
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>

            {/* Publish Button */}
            <Button
              onClick={handlePublish}
              disabled={isPublishing}
            >
              <Globe className="h-4 w-4 mr-2" />
              {isPublishing ? 'Publication...' : site?.status === 'published' ? 'Republier' : 'Publier'}
            </Button>
          </div>
        </header>

        {/* Builder Content */}
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar - Block palette */}
            {!isPreviewMode && (
              <div className="w-64 bg-white border-r flex flex-col">
                <PagesList />
                <div className="flex-1 overflow-y-auto">
                  <BlockPalette />
                </div>
              </div>
            )}

            {/* Main canvas */}
            <div className="flex-1 overflow-auto bg-gray-100 p-4">
              <div
                className={cn(
                  'mx-auto bg-white shadow-lg transition-all duration-300 min-h-full',
                  devicePreview === 'desktop' && 'w-full max-w-6xl',
                  devicePreview === 'tablet' && 'w-[768px]',
                  devicePreview === 'mobile' && 'w-[375px]'
                )}
              >
                <SortableContext
                  items={blocks.map((b) => b.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <BuilderCanvas />
                </SortableContext>
              </div>
            </div>

            {/* Right sidebar - Properties panel */}
            {!isPreviewMode && selectedBlockId && (
              <div className="w-80 bg-white border-l overflow-y-auto">
                <PropertiesPanel />
              </div>
            )}
          </div>

          <DragOverlay>
            {activeId && (
              <div className="bg-white shadow-lg rounded-lg p-4 opacity-80">
                Déplacement...
              </div>
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </TooltipProvider>
  );
}
